import { useEffect, useRef, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../contexts/AuthContext';

const SESSION_MAX_AGE_MS = 28800 * 1000;
const WARNING_BEFORE_MS = 15 * 60 * 1000;
const LOGIN_TIME_KEY = 'session_login_time';

export function useSessionExpiryWarning() {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const refreshAuth = authContext?.refreshAuth;
  const warningTimerRef = useRef(null);

  const extendSession = useCallback(() => {
    if (refreshAuth) {
      refreshAuth().then(() => {
        sessionStorage.setItem(LOGIN_TIME_KEY, String(Date.now()));
        toast.dismiss('session-expiry-warning');
        toast.success('Session extended.', { autoClose: 3000 });
      });
    }
  }, [refreshAuth]);

  useEffect(() => {
    if (!user) {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      sessionStorage.removeItem(LOGIN_TIME_KEY);
      return;
    }

    let loginTime = Number(sessionStorage.getItem(LOGIN_TIME_KEY));
    if (!loginTime || loginTime > Date.now()) {
      loginTime = Date.now();
      sessionStorage.setItem(LOGIN_TIME_KEY, String(loginTime));
    }

    const elapsed = Date.now() - loginTime;
    const warningAt = SESSION_MAX_AGE_MS - WARNING_BEFORE_MS - elapsed;

    if (warningAt <= 0) return;

    warningTimerRef.current = setTimeout(() => {
      toast.warn(
        ({ closeToast }) => (
          <div>
            <div>Your session expires in 15 minutes.</div>
            <button
              onClick={(e) => { e.stopPropagation(); extendSession(); }}
              style={{
                marginTop: 8,
                padding: '4px 12px',
                background: '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            >
              Extend Session
            </button>
          </div>
        ),
        {
          toastId: 'session-expiry-warning',
          autoClose: false,
        }
      );
    }, warningAt);

    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [user, extendSession]);
}
